import * as React from 'react';
import {RouteComponentProps} from "react-router";
import {Checklist, ChecklistRelation, ChecklistState, ErrorResponse, Item, SaveChecklist} from "../api/dtos/dtos";
import {
  ApplicationContextConsumer,
  bindLoadingHandler,
  LoadingOverlayHandler,
  withLoadingOverlay,
} from "../app/ApplicationContext";
import {ErrorAlert} from "../common/ErrorAlert";
import ChecklistForm from "./ChecklistForm";
import furoApi from "../api/FuroApi";
import {
  showConfirm,
  showDeleteConfirm,
  showDeleteSuccessNotification,
  showSaveSuccessNotification,
  showSuccessNotification
} from "../utils/dialogs";
import {idFromPathParams} from "../utils/router-utils";
import ButtonBar from "../common/ButtonBar";
import {Button, Tooltip} from "antd";
import locales from "../locales";
import {setAppHeaderSubtitle} from "../app/AppHeader";
import ItemDetailsBox from "./ItemDetailsBox";
import {formatDateTime} from "../utils/date-utils";
import ChecklistImagesForm from "./ChecklistImagesForm";
import {ChecklistFormMode} from "./dtos";
import {
  AlertController,
  bindAlertController,
  GlobalAlertContextConsumer
} from "../app/alert/GlobalAlertContextProvider";
import ChecklistStateLabel from "./ChecklistStateLabel";
import {Link} from "react-router-dom";
import * as paths from "../paths";
import accountProvider from "../account/AccountProvider";

interface ChecklistDetailsPageState {
  error?: ErrorResponse;
  item?: Item,
  rentalChecklist?: Checklist;
  returnalChecklist?: Checklist;
  editable: boolean;
  checklistId: number;
}

class ChecklistDetailsPage extends React.Component<RouteComponentProps, ChecklistDetailsPageState> {

  public readonly loadingHandler?: LoadingOverlayHandler;
  public readonly alertController?: AlertController;

  constructor(props: RouteComponentProps) {
    super(props);
    const id = idFromPathParams(this.props);
    this.state = {
      editable: false,
      checklistId: id,
    };
  }

  public componentDidMount() {
    this.loadChecklist();
  }

  public render(): React.ReactNode {

    const {editable, rentalChecklist, returnalChecklist, item, error} = this.state;

    const checklist = returnalChecklist ?? rentalChecklist;
    const attachmentsEditable = checklist?.acceptedAt === null;
    const acceptedAt = checklist?.acceptedAt;
    const sendoutAt = checklist?.sendoutAt;
    const ignored = checklist?.state === ChecklistState.IGNORED;

    return (
      <>
        <ApplicationContextConsumer>
          {({loadingHandler}) => bindLoadingHandler(loadingHandler, this)}
        </ApplicationContextConsumer>

        <GlobalAlertContextConsumer>
          {(state) => bindAlertController(state.checklist, this)}
        </GlobalAlertContextConsumer>


        {checklist && item && this.renderChecklistInfo(checklist, item)}

        <ButtonBar style={{marginBottom: 24}}>
          {accountProvider.isAdmin() &&
              <Tooltip title={ignored ? locales.checklist_ignore_revert_tooltip : locales.checklist_ignore_tooltip}>
                  <Button
                      type="default"
                      onClick={ignored ? this.onIgnoreRevert : this.onIgnore}
                      disabled={(sendoutAt !== null ?? true) || error !== undefined}
                  >
                    {ignored ? locales.checklist_ignore_revert : locales.checklist_ignore}
                  </Button>
              </Tooltip>
          }
          {!ignored &&
              <ButtonBar>
                  <Button
                      type="danger"
                      icon="delete"
                      onClick={this.onDelete}
                      disabled={(sendoutAt !== null ?? true) || !editable || error !== undefined}
                  >
                    {locales.delete}
                  </Button>

                  <Button
                      type="primary"
                      icon="edit"
                      onClick={this.onEdit}
                      disabled={(acceptedAt !== null ?? false) || editable || error !== undefined || ignored}
                  >
                    {locales.edit}
                  </Button>
              </ButtonBar>}
        </ButtonBar>
        {!ignored &&
            <>
                <ChecklistForm
                    mode={returnalChecklist ? ChecklistFormMode.EDIT_RETURNAL : ChecklistFormMode.EDIT_RENTAL}
                    editable={editable}
                    rentalItemId={undefined}
                    rentalChecklist={rentalChecklist}
                    returnalChecklist={returnalChecklist}
                    onFormCancel={this.onCancel}
                    onFormSubmit={this.onSave}
                />

              {error && <ErrorAlert style={{marginTop: '16px'}} error={error}/>}

                <ChecklistImagesForm
                    rentalChecklist={rentalChecklist}
                    returnalChecklist={returnalChecklist}
                    editable={attachmentsEditable}
                    onImagesChange={this.onImagesChanged}
                />

                <ButtonBar style={{marginBottom: 24}} align={'center'}>
                    <Button
                        type="default"
                        onClick={this.onBack}
                    >
                      {locales.back}
                    </Button>
                </ButtonBar>
            </>
        }
      </>
    );
  }

  private onBack = (): void => {
    this.props.history.goBack();
  }

  private renderChecklistInfo = (checklist: Checklist, item: Item): React.ReactNode => {
    const acceptedAt = checklist.acceptedAt;
    const sendoutAt = checklist.sendoutAt;
    const checklistState = checklist.state;
    const rentalInfo = checklist.returnal ?? checklist.rental;
    const rentalIdLabel = checklist.returnal ? locales.return : locales.rental;

    return (
      <table style={{marginBottom: 16}}>
        <tbody>
        {item && this.renderInfoRow(locales.item, <ItemDetailsBox item={item} style={{marginBottom: 24}}/>)}
        {rentalInfo && this.renderInfoRow(rentalIdLabel, this.renderRentalId(rentalInfo))}
        {rentalInfo && this.renderInfoRow('', formatDateTime(rentalInfo.createdAt))}
        {sendoutAt && this.renderInfoRow(locales.checklist_sent_out, formatDateTime(sendoutAt))}
        {sendoutAt && this.renderInfoRow(locales.checklist_accepted_at, acceptedAt ? formatDateTime(acceptedAt) : '-')}
        {checklistState && this.renderInfoRow(locales.state, <ChecklistStateLabel state={checklistState}/>)}
        </tbody>
      </table>
    );
  }

  private renderInfoRow = (label: string, value: React.ReactNode): React.ReactNode => {
    return (
      <tr>
        <td style={{textAlign: 'right', paddingRight: 8, verticalAlign: 'top'}}>
          <b>{label}</b>
        </td>
        <td>{value}</td>
      </tr>
    );
  }

  private renderRentalId = (relation: ChecklistRelation) => {
    return <Link to={paths.viewRentalPath(relation.id)}>{relation.idString}</Link>
  };


  private onEdit = () => {
    this.setState({editable: true});
  };

  private onCancel = () => {
    this.setState({editable: false, error: undefined});
  };

  private loadChecklist = () => {
    const promise = furoApi.checklist.get(this.state.checklistId)
      .then(this.onChecklistLoaded)
      .catch(this.onApiError)
    withLoadingOverlay(promise, this.loadingHandler);
  }

  private onChecklistLoaded = (c: Checklist) => {
    if (c.returnal !== null) {
      this.setState({
        editable: false,
        returnalChecklist: c,
        item: c.rentalItem.item,
        error: undefined,
      }, () => {
        const rentalChecklistId = c.rentalItem.rentalChecklistId;
        if (rentalChecklistId !== null) {
          const promise = furoApi.checklist.get(rentalChecklistId)
            .then(this.onChecklistLoaded)
            .catch(this.onApiError);
          withLoadingOverlay(promise, this.loadingHandler);
        }
      });
    } else {
      this.setState({
        editable: false,
        rentalChecklist: c,
        item: c.rentalItem.item,
        error: undefined,
      })
    }
    setAppHeaderSubtitle(this.props, c.rental?.idString ?? c.returnal?.idString ?? "");
  };

  private onSave = (c: SaveChecklist) => {
    const promise = furoApi.checklist.save(this.state.checklistId, c)
      .then(this.onChecklistSaved)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onChecklistSaved = () => {
    showSaveSuccessNotification();
    this.loadChecklist();
  }

  private onDelete = () => {
    showDeleteConfirm(this.deleteChecklist);
  };

  private deleteChecklist = () => {
    const id = this.state.checklistId;
    const {returnalChecklist, rentalChecklist} = this.state;
    if (rentalChecklist?.id !== id && returnalChecklist?.id !== id) {
      return;
    }

    const promise = furoApi.checklist.delete(id)
      .then(this.onChecklistDeleted)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onChecklistDeleted = () => {
    showDeleteSuccessNotification();
    this.onImagesChanged();
    this.props.history.goBack();
  }

  private onIgnore = () => {
    showConfirm(locales.checklist_ignore_confirm, this.ignoreChecklist);
  };

  private ignoreChecklist = () => {
    const id = this.state.checklistId;
    const {returnalChecklist, rentalChecklist} = this.state;
    if (rentalChecklist?.id !== id && returnalChecklist?.id !== id) {
      return;
    }

    const cl = returnalChecklist ?? rentalChecklist;

    const saveChecklistDto: SaveChecklist = {
      id,
      rentalItemId: cl?.rentalItem.id!,
      rentalId: cl!.rental?.id ?? undefined,
      returnalId: cl!.returnal?.id ?? undefined,
      properties: cl!.properties,
      comment: cl!.comment,
      ignored: true,
    };

    const promise = furoApi.checklist.save(id, saveChecklistDto)
      .then(this.onChecklistIngored)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  };

  private onChecklistIngored = () => {
    showSuccessNotification(locales.checklist_ignore_success);
    this.alertController?.refreshAlertCount();
    this.loadChecklist();
  }


  private onIgnoreRevert = () => {
    showConfirm(locales.checklist_ignore_revert_confirm, this.ignoreChecklistRevert);
  };


  private ignoreChecklistRevert = () => {
    const id = this.state.checklistId;
    const {returnalChecklist, rentalChecklist} = this.state;
    if (rentalChecklist?.id !== id && returnalChecklist?.id !== id) {
      return;
    }

    const promise = furoApi.checklist.delete(id)
      .then(this.onChecklistIgnoreReverted)
      .catch(this.onApiError);
    withLoadingOverlay(promise, this.loadingHandler);
  }

  private onChecklistIgnoreReverted = () => {
    const {returnalChecklist, rentalChecklist} = this.state;

    const cl = returnalChecklist ?? rentalChecklist;
    showSuccessNotification(locales.checklist_ignore_revert_success)
    this.alertController?.refreshAlertCount();

    this.props.history.replace(paths.createChecklistPath({
      rentalItemId: cl?.rentalItem.id!,
      rentalId: cl!.rental?.id,
      returnalId: cl!.returnal?.id,
    }));
  }

  private onApiError = (error: ErrorResponse) => {
    this.setState({error});
  }

  private onImagesChanged = () => {
    this.loadChecklist();
    this.alertController?.refreshAlertCount();
  }
}

export default ChecklistDetailsPage;


// WEBPACK FOOTER //
// ./src/checklist/ChecklistDetailsPage.tsx